import { FileModel } from '@/models/file'
import { UserModel } from '@/models/user'
import { ProspectModel } from '@/models/prospect'
import { BaseModel } from '@/models/baseModel'
import { ForumReactionModel } from '@/models/forum/forumReaction'
import { ForumResponseMentionModel } from '@/models/forum/forumResponseMention'

export class ForumResponseModel extends BaseModel {
  static _mentionRegex = /{{mention:(.*?)}}/gi

  static _encodeMentionPayload(obj) {
    const json = JSON.stringify(JSON.stringify(obj))
    return json
  }

  static _decodeMentionPayload(s) {
    return JSON.parse(JSON.parse(s))
  }

  constructor(responseData = {}) {
    super()
    const {
      id,
      text,
      mentions,
      files,
      user,
      prospect,
      createdAt,
      updatedAt,
      deletedAt,
      parentResponseId,
      replyToId,
      chatId,
      threadId,
      prospectId,
      userId,
      isAnonymous,
      anonymousId,
      anonymousState,
      position,
      repliesCount,
      reactions,
      replies,
      isRead,
      isOwnerModerator,
      isPinned,
      isSaved
    } = responseData || {}

    this.id = id || null
    this.text = text || ''
    this.mentions = Array.isArray(mentions)
      ? mentions.map((mention) => new ForumResponseMentionModel(mention))
      : []
    this.files = Array.isArray(files) ? files.map((file) => new FileModel(file)) : []
    this.user = user ? new UserModel(user) : null
    this.prospect = prospect ? new ProspectModel(prospect) : null
    this.createdAt = createdAt || null
    this.updatedAt = updatedAt || null
    this.deletedAt = deletedAt || null

    this.chatId = chatId || null
    this.parentResponseId = parentResponseId || null
    this.replyToId = replyToId || null
    this.threadId = threadId || null
    this.prospectId = prospectId || null
    this.userId = userId || null
    this.isAnonymous = isAnonymous || false
    this.anonymousId = anonymousId || null
    this.anonymousState = anonymousState || false
    this.position = position || null
    this.repliesCount = repliesCount ?? null
    this.reactions = reactions?.map((reaction) => new ForumReactionModel(reaction)) || []
    this.replies = Array.isArray(replies) ? replies.map((reply) => new ForumResponseModel(reply)) : []
    this.isRead = isRead ?? true
    this.isOwnerModerator = isOwnerModerator ?? false
    this.isPinned = isPinned ?? false
    this.isSaved = isSaved ?? false
  }

  get mentionProcessedText() {
    let restoredText = this.text ?? ''
    const serverMentions = Array.isArray(this.mentions) ? this.mentions : []

    const ESC = '__ESCAPED_DOLLAR__'
    restoredText = restoredText.replace(/\\\$/g, ESC)

    restoredText = restoredText.replace(/\$(\d+)/g, (m, numStr) => {
      const idx = Number(numStr) - 1
      const data = serverMentions[idx]
      if (!data) return m
      const encoded = ForumResponseModel._encodeMentionPayload(data)
      return `{{mention:${encoded}}}`
    })

    restoredText = restoredText.replace(new RegExp(ESC, 'g'), '$')
    return restoredText
  }

  set mentionProcessedText(text) {
    const M_PLACEHOLDER = (n) => `__MENTION_${n}__`
    const mentions = []
    let idx = 0

    let intermediate = (text || '').replace(ForumResponseModel._mentionRegex, (_match, payload) => {
      idx += 1
      const parsed = ForumResponseModel._decodeMentionPayload(payload)
      mentions.push(new ForumResponseMentionModel(parsed))
      return M_PLACEHOLDER(idx)
    })

    // Replace `(?<!\\)\$(\d+)` — negative lookbehind not supported on iOS < 16.4
    // Use placeholder approach instead (same pattern as get mentionProcessedText)
    const ESC_DOLLAR_SENTINEL = '\x00_ESC_DOLLAR_\x00'
    intermediate = intermediate.replace(/\\\$/g, ESC_DOLLAR_SENTINEL)
    intermediate = intermediate.replace(/\$(\d+)/g, '\\$$1')
    intermediate = intermediate.replace(new RegExp(ESC_DOLLAR_SENTINEL, 'g'), '\\$')

    const textWithDollars = intermediate.replace(/__MENTION_(\d+)__/g, (_m, n) => `$${n}`)

    this.text = textWithDollars
    this.mentions = mentions
  }

  static parseFromApi({ response, chatId, threadId, user, prospect, users, prospects }) {
    const _users = users?.map((user) => UserModel.parseFromApi({ user })) || []
    const _prospects = prospects?.map((prospect) => ProspectModel.parseFromApi({ prospect })) || []

    const _user = user
      ? UserModel.parseFromApi({ user })
      : _users.find((u) => u.id === response?.userId) || null
    const _prospect = prospect
      ? ProspectModel.parseFromApi({ prospect })
      : _prospects.find((p) => p.id === response?.prospectId) || null

    return new ForumResponseModel({
      id: response?.id,
      text: response?.text,
      mentions:
        response?.mentions?.map((mention) => {
          const user = users?.find((u) => u.id === mention.userId)
          const prospect = prospects?.find((p) => p.id === mention.prospectId)
          return ForumResponseMentionModel.parseFromApi({
            mention,
            user,
            prospect
          })
        }) || [],
      chatId,
      parentResponseId: response?.parentResponseId,
      replyToId: response?.replyToId,
      threadId,
      userId: user?.id || response?.userId || null,
      prospectId: prospect?.id || response?.prospectId || null,
      files:
        response?.files
          ?.map((file) => FileModel.parseFromApi({ file }))
          ?.sort((a, b) => a?.order - b?.order) || [],
      createdAt: response?.createdAt,
      updatedAt: response?.updatedAt,
      deletedAt: response?.deletedAt,
      user: _user || null,
      prospect: _prospect || null,
      isAnonymous: response?.isAnonymous,
      anonymousId: response?.anonymousId,
      anonymousState: response?.anonymousState,
      position: response?.position,
      repliesCount: response?.repliesCount ?? null,
      reactions:
        response?.reactions?.map((reaction) =>
          ForumReactionModel.parseFromApi({ reaction, users, prospects })
        ) || [],
      replies:
        response?.replies?.map((reply) =>
          ForumResponseModel.parseFromApi({ response: reply, chatId, threadId, users, prospects })
        ) || [],
      isRead: response?.isRead ?? true,
      isOwnerModerator: response?.isOwnerModerator,
      isPinned: response?.isPinned ?? false,
      isSaved: response?.isSaved ?? false
    })
  }

  convertToApi() {
    const { id, text, isAnonymous, isPinned } = this
    return {
      id: id || undefined,
      text,
      parentResponseId: this.parentResponseId,
      replyToId: this.replyToId,
      fileIdList: this.files?.map((file) => file.id) || undefined,
      isAnonymous,
      isPinned,
      mentions: this.mentions?.map((mention) => mention.convertToApi()) || []
    }
  }
}
